import { useEffect, useRef, useState } from 'react'
import useNotificationsContext from '@contexts/notificationsContext'
import { useAuthContext } from '@contexts/authContext'
import axios from '@utils/webRequests'
import minsAgo from '@utils/minsAgo'

const useUnseenNotifications = (selectedItem, selectedItemRef) => {
  const { authenticated, state: authState } = useAuthContext()
  const isAnonymous = authState?.user?.isAnonymous
  const { setTimeSinceLastLoad } = useNotificationsContext()
  const [unseenNotifications, setUnseenNotifications] = useState(0)
  const timeoutRef = useRef()
  const firstRender = useRef(true)
  const canGetUnseenNotifications = () => (
    // must be authenticated first
    authenticated
    // user must not be anonymous, because anonymous users can't post anything
    && !isAnonymous
    // the selection must exist
    && selectedItemRef.current
    // the selection must not be notifications screen
    && selectedItemRef.current !== 'notifications'
  )

  // When we click on the notifications tab, set unseen notifications to zero
  useEffect(() => {
    if (selectedItem === 'notifications') {
      if (unseenNotifications > 0) setUnseenNotifications(0);
    }
  }, [selectedItem])

  // get notifications count on initial render if not on notifications screen
  useEffect(() => {
    if (firstRender.current && authenticated && !isAnonymous && selectedItem) {
      if (canGetUnseenNotifications()) {
        checkUnseenNotifications()
      }
      firstRender.current = false
    }
  }, [selectedItem, authenticated])

  // sets timeout to recheck unseen notifications once every five minutes with standoff +1 min
  useEffect(() => {
    if (authenticated && !isAnonymous) {
      // check for new notifications every five minutes increasing to ten if none found
      checkUnseenTimeout(1)
      // clear interval on unmount, if that's actually happening
      return () => clearTimeout(timeoutRef.current);
    }
  }, [authenticated, isAnonymous])

  const checkUnseenTimeout = (minutes) => {
    timeoutRef.current = setTimeout(() => {
      if (canGetUnseenNotifications()) {
        // if the user isn't viewing notifications, check unseen notifications
        checkUnseenNotifications().then((res) => {
          let nextMinutes = 1
          if (!res?.data || res?.data == unseenNotifications) {
            // if we have no new notifications, or the number hasn't changed
            // increase time until next poll by 1 min up to 7
            nextMinutes = minutes >= 7 ? 7 : minutes + 1
          }
          // if we have new notifications, or they've changed
          // then we reset the polling rate to 
          checkUnseenTimeout(nextMinutes)
        })
        .catch(() => checkUnseenTimeout(1))
      } else {
        // if the user is currently viewing the tab, reset the fetch rate
        // without checking notifications
        checkUnseenTimeout(1)
      }
    }, minutes * 60_000);
  }

  const checkUnseenNotifications = () =>
    axios.get('/api/my/notifications/check_unseen')
      .then((res) => {
        setUnseenNotifications(res.data)
        if (res?.data > 0 && res?.data) {
          // Setting this causes the notifications to automtically re-fetch
          // the next time we navigate to the notifications screen
          // NOTE: it has to be set greater than the timestamp that causes polling (hence 5 minutes)
          setTimeSinceLastLoad(minsAgo(5))
        }
        return res
      })
      .catch(console.log)

  return [unseenNotifications, setUnseenNotifications]
}

export default useUnseenNotifications
